/**
 * B4: Manager Assessment Page - 6-Step Flow
 * 
 * Steps:
 * 1. Verify Identity - Confirm relationship to employee
 * 2. Role Details - Review/amend job title, dates, responsibilities
 * 3. Performance Ratings - Star ratings for punctuality, deadline, initiative, adaptability
 * 4. Strengths & Growth - Text areas with word/char counters
 * 5. Collaboration & Closing - Team perception, would work with again, closing statement
 * 6. Attestation - EEOC compliance checkbox and submit
 * 
 * Also includes: Decline path with reason
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Star, ChevronLeft, ChevronRight, AlertCircle, CheckCircle, X, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Star Rating Component
const StarRating = ({ value, onChange, label, required }) => {
  const [hover, setHover] = useState(0);
  
  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-300 mb-2">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            onMouseEnter={() => setHover(star)}
            onMouseLeave={() => setHover(0)}
            className="p-1 transition-transform hover:scale-110"
          >
            <Star
              className={`w-8 h-8 ${
                star <= (hover || value)
                  ? 'fill-yellow-400 text-yellow-400'
                  : 'text-gray-500'
              }`}
            />
          </button>
        ))}
        <span className="ml-2 text-gray-400 self-center">
          {value ? `${value}/5` : 'Not rated'}
        </span>
      </div>
    </div>
  );
};

// Text Area with Counter
const TextAreaWithCounter = ({ value, onChange, label, maxChars, maxWords, placeholder, required }) => {
  const wordCount = value ? value.trim().split(/\s+/).filter(w => w).length : 0;
  const charCount = value ? value.length : 0;
  
  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-300 mb-2">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      <textarea
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={4}
        className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-transparent"
      />
      <div className="flex justify-between text-xs text-gray-500 mt-1">
        <span>{wordCount} words {maxWords && `/ ${maxWords} max`}</span>
        <span>{charCount} chars {maxChars && `/ ${maxChars} max`}</span>
      </div>
    </div>
  );
};

// Progress Rail
const ProgressRail = ({ currentStep, totalSteps, stepLabels }) => {
  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-2">
        {stepLabels.map((label, index) => (
          <div
            key={index}
            className={`flex flex-col items-center ${
              index <= currentStep ? 'text-coral-400' : 'text-gray-500'
            }`}
          >
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                index < currentStep
                  ? 'bg-coral-500 text-white'
                  : index === currentStep
                  ? 'bg-coral-500/20 border-2 border-coral-500 text-coral-400'
                  : 'bg-gray-700 text-gray-500'
              }`}
            >
              {index < currentStep ? <CheckCircle className="w-5 h-5" /> : index + 1}
            </div>
            <span className="text-xs mt-1 hidden sm:block">{label}</span>
          </div>
        ))}
      </div>
      <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-coral-500 to-coral-400 transition-all duration-300"
          style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
        />
      </div>
    </div>
  );
};

// Decline Modal
const DeclineModal = ({ isOpen, onClose, onConfirm, loading }) => {
  const [reason, setReason] = useState('');
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl max-w-md w-full p-6 border border-gray-700">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-white">Decline Verification</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-gray-400 mb-4">
          Please provide a reason for declining this verification request. This will be shared with the employee.
        </p>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason for declining (optional but recommended)"
          rows={3}
          className="w-full bg-gray-900 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 mb-4"
        />
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(reason)}
            disabled={loading}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-500 transition-colors disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Confirm Decline'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function ManagerAssessPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  
  const [currentStep, setCurrentStep] = useState(0);
  const [prefill, setPrefill] = useState(null);
  const [recordId, setRecordId] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    // Step 1: Identity
    relationship_confirmed: false,
    relationship_type: '',
    
    // Step 2: Role Details
    job_title_correct: true,
    amended_job_title: '',
    dates_correct: true,
    amended_start_date: '',
    amended_end_date: '',
    daily_responsibilities: [],
    
    // Step 3: Performance Ratings
    punctuality_rating: 0,
    deadline_rating: 0,
    initiative_rating: 0,
    adaptability_rating: 0,
    work_ethic_note: '',
    
    // Step 4: Strengths & Growth
    strengths: '',
    growth_areas: '',
    
    // Step 5: Collaboration & Closing
    peer_team_perception: '',
    collaboration_style: '',
    collaboration_note: '',
    would_work_with_again: null,
    closing_statement: '',
    
    // Step 6: Attestation
    eeoc_attestation_accepted: false,
  });
  
  const stepLabels = ['Identity', 'Role', 'Ratings', 'Strengths', 'Closing', 'Submit'];
  
  useEffect(() => {
    fetchPrefill();
  }, [token]);
  
  const fetchPrefill = async () => {
    try {
      const response = await fetch(`${API_URL}/api/dual-consent/manager/${token}`);
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Invalid or expired token');
      }
      const data = await response.json();
      setPrefill(data.prefill);
      setRecordId(data.record_id);
      setFormData(prev => ({
        ...prev,
        amended_job_title: data.prefill?.job_title || '',
        amended_start_date: data.prefill?.start_date || '',
        amended_end_date: data.prefill?.end_date || '',
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDecline = async (reason) => {
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/dual-consent/manager/${token}/decline`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to decline');
      }
      setShowDeclineModal(false);
      setSuccess(true);
      setError('You have declined this verification request.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/dual-consent/manager/${token}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          strengths: formData.strengths.split(',').map(s => s.trim()).filter(Boolean),
          growth_areas: formData.growth_areas.split(',').map(s => s.trim()).filter(Boolean),
        }),
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to submit assessment');
      }
      
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };
  
  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return formData.relationship_confirmed && formData.relationship_type;
      case 1:
        return true;
      case 2:
        return formData.punctuality_rating > 0 && formData.deadline_rating > 0 &&
               formData.initiative_rating > 0 && formData.adaptability_rating > 0;
      case 3:
        return formData.strengths.length > 0;
      case 4:
        return formData.would_work_with_again !== null && formData.closing_statement.length > 0;
      case 5:
        return formData.eeoc_attestation_accepted;
      default:
        return false;
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-coral-500 animate-spin" />
      </div>
    );
  }
  
  if (success) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-gray-800 rounded-xl max-w-md w-full p-8 text-center border border-gray-700">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Thank You!</h1>
          <p className="text-gray-400">
            Your assessment has been submitted successfully. The employee will be notified.
          </p>
        </div>
      </div>
    );
  }
  
  if (error && !prefill) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-gray-800 rounded-xl max-w-md w-full p-8 text-center border border-red-500/50">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Invalid Link</h1>
          <p className="text-gray-400">{error}</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-900 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Manager Assessment</h1>
          <p className="text-gray-400">
            Verify employment details for <span className="text-coral-400">{prefill?.employee_name || 'this employee'}</span>
          </p>
          <p className="text-sm text-gray-500 mt-1">
            {prefill?.job_title} at {prefill?.employer_name}
          </p>
        </div>
        
        {/* Progress Rail */}
        <ProgressRail currentStep={currentStep} totalSteps={6} stepLabels={stepLabels} />
        
        {/* Error Banner */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className="text-red-400">{error}</p>
          </div>
        )}
        
        {/* Step Content */}
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 mb-6">
          {/* Step 1: Identity Verification */}
          {currentStep === 0 && (
            <div>
              <h2 className="text-xl font-semibold text-white mb-4">Verify Your Identity</h2>
              <p className="text-gray-400 mb-6">
                Please confirm your professional relationship with {prefill?.employee_name || 'this employee'}.
              </p>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Your relationship to this employee <span className="text-red-400">*</span>
                </label>
                <select
                  value={formData.relationship_type}
                  onChange={(e) => updateField('relationship_type', e.target.value)}
                  className="w-full bg-gray-900 border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-coral-500"
                >
                  <option value="">Select relationship</option>
                  <option value="direct_manager">Direct Manager</option>
                  <option value="skip_level_manager">Skip-Level Manager</option>
                  <option value="team_lead">Team Lead</option>
                  <option value="department_head">Department Head</option>
                  <option value="hr_manager">HR Manager</option>
                  <option value="other">Other Supervisor</option>
                </select>
              </div>
              
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.relationship_confirmed}
                  onChange={(e) => updateField('relationship_confirmed', e.target.checked)}
                  className="mt-1 w-5 h-5 rounded border-gray-600 bg-gray-900 text-coral-500 focus:ring-coral-500"
                />
                <span className="text-gray-300">
                  I confirm that I was this employee&#39;s supervisor during their employment and can accurately assess their performance.
                </span>
              </label>
            </div>
          )}
          
          {/* Step 2: Role Details */}
          {currentStep === 1 && (
            <div>
              <h2 className="text-xl font-semibold text-white mb-4">Role Details</h2>
              <p className="text-gray-400 mb-6">
                Review and verify the employment details below.
              </p>
              
              <div className="bg-gray-900 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-500">Job Title</p>
                <p className="text-white font-medium">{prefill?.job_title}</p>
              </div>
              
              <label className="flex items-center gap-3 mb-4 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.job_title_correct}
                  onChange={(e) => updateField('job_title_correct', e.target.checked)}
                  className="w-5 h-5 rounded border-gray-600 bg-gray-900 text-coral-500 focus:ring-coral-500"
                />
                <span className="text-gray-300">Job title is correct</span>
              </label>
              
              {!formData.job_title_correct && (
                <input
                  type="text"
                  value={formData.amended_job_title}
                  onChange={(e) => updateField('amended_job_title', e.target.value)}
                  placeholder="Enter correct job title"
                  className="w-full bg-gray-900 border border-gray-600 rounded-lg px-4 py-3 text-white mb-4 focus:outline-none focus:ring-2 focus:ring-coral-500"
                />
              )}
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-gray-900 rounded-lg p-4">
                  <p className="text-sm text-gray-500">Start Date</p>
                  <p className="text-white font-medium">{prefill?.start_date}</p>
                </div>
                <div className="bg-gray-900 rounded-lg p-4">
                  <p className="text-sm text-gray-500">End Date</p>
                  <p className="text-white font-medium">{prefill?.end_date || 'Current'}</p>
                </div>
              </div>
              
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.dates_correct}
                  onChange={(e) => updateField('dates_correct', e.target.checked)}
                  className="w-5 h-5 rounded border-gray-600 bg-gray-900 text-coral-500 focus:ring-coral-500"
                />
                <span className="text-gray-300">Employment dates are correct</span>
              </label>
            </div>
          )}
          
          {/* Step 3: Performance Ratings */}
          {currentStep === 2 && (
            <div>
              <h2 className="text-xl font-semibold text-white mb-4">Performance Ratings</h2>
              <p className="text-gray-400 mb-6">
                Rate the employee&#39;s performance in each area.
              </p>
              
              <StarRating
                value={formData.punctuality_rating}
                onChange={(v) => updateField('punctuality_rating', v)}
                label="Punctuality & Attendance"
                required
              />
              
              <StarRating
                value={formData.deadline_rating}
                onChange={(v) => updateField('deadline_rating', v)}
                label="Meeting Deadlines"
                required
              />
              
              <StarRating
                value={formData.initiative_rating}
                onChange={(v) => updateField('initiative_rating', v)}
                label="Initiative & Proactivity"
                required
              />
              
              <StarRating
                value={formData.adaptability_rating}
                onChange={(v) => updateField('adaptability_rating', v)}
                label="Adaptability"
                required
              />
              
              <TextAreaWithCounter
                value={formData.work_ethic_note}
                onChange={(v) => updateField('work_ethic_note', v)}
                label="Work Ethic Notes"
                placeholder="Any additional notes about their work ethic..."
                maxChars={500}
              />
            </div>
          )}
          
          {/* Step 4: Strengths & Growth */}
          {currentStep === 3 && (
            <div>
              <h2 className="text-xl font-semibold text-white mb-4">Strengths & Growth Areas</h2>
              <p className="text-gray-400 mb-6">
                Describe the employee&#39;s key strengths and areas for growth.
              </p>
              
              <TextAreaWithCounter
                value={formData.strengths}
                onChange={(v) => updateField('strengths', v)}
                label="Key Strengths"
                placeholder="e.g., Leadership, Communication, Problem-solving (comma-separated)"
                maxWords={100}
                required
              />
              
              <TextAreaWithCounter
                value={formData.growth_areas}
                onChange={(v) => updateField('growth_areas', v)}
                label="Areas for Growth"
                placeholder="e.g., Time management, Public speaking (comma-separated)"
                maxWords={100}
              />
            </div>
          )}
          
          {/* Step 5: Collaboration & Closing */}
          {currentStep === 4 && (
            <div>
              <h2 className="text-xl font-semibold text-white mb-4">Collaboration & Closing Statement</h2>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  How was this employee perceived by peers/team?
                </label>
                <select
                  value={formData.peer_team_perception}
                  onChange={(e) => updateField('peer_team_perception', e.target.value)}
                  className="w-full bg-gray-900 border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-coral-500"
                >
                  <option value="">Select perception</option>
                  <option value="highly_respected">Highly Respected</option>
                  <option value="well_liked">Well Liked</option>
                  <option value="neutral">Neutral</option>
                  <option value="some_friction">Some Friction</option>
                  <option value="challenging">Challenging</option>
                </select>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Collaboration style
                </label>
                <select
                  value={formData.collaboration_style}
                  onChange={(e) => updateField('collaboration_style', e.target.value)}
                  className="w-full bg-gray-900 border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-coral-500"
                >
                  <option value="">Select style</option>
                  <option value="team_player">Team Player</option>
                  <option value="independent">Independent Worker</option>
                  <option value="leader">Natural Leader</option>
                  <option value="supportive">Supportive</option>
                  <option value="flexible">Flexible/Adaptable</option>
                </select>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Would you work with this person again? <span className="text-red-400">*</span>
                </label>
                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={() => updateField('would_work_with_again', true)}
                    className={`flex-1 py-3 rounded-lg border transition-colors ${
                      formData.would_work_with_again === true
                        ? 'bg-green-500/20 border-green-500 text-green-400'
                        : 'bg-gray-900 border-gray-600 text-gray-400 hover:border-gray-500'
                    }`}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => updateField('would_work_with_again', false)}
                    className={`flex-1 py-3 rounded-lg border transition-colors ${
                      formData.would_work_with_again === false
                        ? 'bg-red-500/20 border-red-500 text-red-400'
                        : 'bg-gray-900 border-gray-600 text-gray-400 hover:border-gray-500'
                    }`}
                  >
                    No
                  </button>
                </div>
              </div>
              
              <TextAreaWithCounter
                value={formData.closing_statement}
                onChange={(v) => updateField('closing_statement', v)}
                label="Closing Statement"
                placeholder="Provide a final summary of this employee&#39;s performance and character..."
                maxChars={1000}
                maxWords={150}
                required
              />
            </div>
          )}
          
          {/* Step 6: Attestation */}
          {currentStep === 5 && (
            <div>
              <h2 className="text-xl font-semibold text-white mb-4">Attestation & Submit</h2>
              
              <div className="bg-gray-900 rounded-lg p-4 mb-6">
                <h3 className="font-medium text-white mb-2">Summary of Your Assessment</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="text-gray-500">Overall Rating:</div>
                  <div className="text-white">
                    {((formData.punctuality_rating + formData.deadline_rating + 
                       formData.initiative_rating + formData.adaptability_rating) / 4).toFixed(1)}/5
                  </div>
                  <div className="text-gray-500">Would Work Again:</div>
                  <div className={formData.would_work_with_again ? 'text-green-400' : 'text-red-400'}>
                    {formData.would_work_with_again ? 'Yes' : 'No'}
                  </div>
                </div>
              </div>
              
              <div className="bg-yellow-500/10 border border-yellow-500/50 rounded-lg p-4 mb-6">
                <h3 className="font-medium text-yellow-400 mb-2">EEOC Compliance Notice</h3>
                <p className="text-sm text-gray-400">
                  This assessment must be based solely on job-related criteria and documented performance. 
                  It must not be influenced by race, color, religion, sex, national origin, age, disability, 
                  or genetic information.
                </p>
              </div>
              
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.eeoc_attestation_accepted}
                  onChange={(e) => updateField('eeoc_attestation_accepted', e.target.checked)}
                  className="mt-1 w-5 h-5 rounded border-gray-600 bg-gray-900 text-coral-500 focus:ring-coral-500"
                />
                <span className="text-gray-300">
                  I attest that this assessment is accurate, based on documented performance, and complies 
                  with EEOC guidelines. I understand this will become part of the employee&#39;s verified 
                  career record and cannot be edited after submission.
                </span>
              </label>
            </div>
          )}
        </div>
        
        {/* Navigation Buttons */}
        <div className="flex gap-4">
          {currentStep > 0 && (
            <button
              onClick={() => setCurrentStep(prev => prev - 1)}
              className="flex items-center gap-2 px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
              Back
            </button>
          )}
          
          <button
            onClick={() => setShowDeclineModal(true)}
            className="px-6 py-3 bg-red-600/20 text-red-400 rounded-lg hover:bg-red-600/30 transition-colors border border-red-600/50"
          >
            Decline
          </button>
          
          <div className="flex-1" />
          
          {currentStep < 5 ? (
            <button
              onClick={() => setCurrentStep(prev => prev + 1)}
              disabled={!canProceed()}
              className="flex items-center gap-2 px-6 py-3 bg-coral-500 text-white rounded-lg hover:bg-coral-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight className="w-5 h-5" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={!canProceed() || submitting}
              className="flex items-center gap-2 px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  Submit Assessment
                </>
              )}
            </button>
          )}
        </div>
      </div>
      
      <DeclineModal
        isOpen={showDeclineModal}
        onClose={() => setShowDeclineModal(false)}
        onConfirm={handleDecline}
        loading={submitting}
      />
    </div>
  );
}
